// src/pages/PainelCliente/PainelClienteHome.js
// Home do cliente: saldo em destaque, avisos, banner, Experience,
// categorias, prêmios e campanhas. As chamadas à API são as mesmas de antes.

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "../../axiosConfig";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  ChevronDown,
  Clock,
  Dumbbell,
  FileEdit,
  Gift,
  Home as IconeCasa,
  Shirt,
  ShoppingCart,
  Smartphone,
  Trophy,
  Tv,
  Utensils,
} from "lucide-react";

import { CarrinhoContext } from "../../CarrinhoContext";
import ModalPremioDetalhe from "../../components/ModalPremioDetalhe";
import ModalSorteioEvento from "../eventos/clientes/sorteio/ModalSorteioEvento";
import {
  calcularPontosPremio,
  estaEmPromocao,
  formatarPontos,
  getImagemUrl,
  lerUsuario,
  normalizarTexto,
  pontosSemPromocao,
  premioDisponivel,
  primeiroNome,
} from "../../utils/tikpontos";
import "./PainelClienteHome.css";

// Ícone e cor de cada categoria, escolhidos pelo nome cadastrado no admin.
// Categorias novas sem regra aqui recebem o ícone de presente.
const ICONES_CATEGORIA = [
  { termos: ["celular", "telefon", "smartphone"], icone: Smartphone, tom: "azul" },
  { termos: ["casa", "jardim", "decora"], icone: IconeCasa, tom: "laranja" },
  { termos: ["tv", "som", "video", "audio"], icone: Tv, tom: "verde" },
  { termos: ["cozinha", "utensil", "eletroport"], icone: Utensils, tom: "teal" },
  { termos: ["moda", "acessorio", "roupa", "vestu"], icone: Shirt, tom: "rosa" },
  { termos: ["fitness", "esporte", "academia"], icone: Dumbbell, tom: "roxo" },
  { termos: ["copa", "futebol", "torcedor"], icone: Trophy, tom: "ceu" },
];

const MAX_CATEGORIAS_HOME = 8;

function visualDaCategoria(nome) {
  const texto = normalizarTexto(nome);
  return (
    ICONES_CATEGORIA.find((c) => c.termos.some((t) => texto.includes(t))) || {
      icone: Gift,
      tom: "azul",
    }
  );
}

const STATUS_RESGATE = {
  pedido: { texto: "Pedido recebido", tom: "" },
  preparacao: { texto: "Em preparação", tom: "laranja" },
  transporte: { texto: "Em transporte", tom: "azul" },
  enviado: { texto: "Enviado", tom: "verde" },
  cancelado: { texto: "Cancelado", tom: "vermelho" },
  rejeitado: { texto: "Cancelado", tom: "vermelho" },
};

const ABAS = [
  { id: "saldo", label: "Cabem no meu saldo" },
  { id: "novidades", label: "Novidades" },
  { id: "populares", label: "Mais pedidos" },
];

const QTD_PREMIOS_HOME = 12;

function formatarData(data) {
  if (!data) return "";
  const d = new Date(data);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("pt-BR");
}

function CartaoPremio({ premio, valorPonto, saldo, onAbrir, onAdicionar }) {
  const pts = calcularPontosPremio(premio, valorPonto);
  const promo = estaEmPromocao(premio);
  const cabe = pts <= saldo;
  const pct = pts > 0 ? Math.min(100, Math.round((saldo / pts) * 100)) : 0;

  return (
    <article className="hm-premio">
      <button type="button" className="hm-premio-abrir" onClick={() => onAbrir(premio)}>
        <span className="hm-premio-foto">
          {premio.imagem ? (
            <img src={getImagemUrl(premio.imagem)} alt="" loading="lazy" />
          ) : (
            <Gift size={34} aria-hidden="true" />
          )}
          {promo && <span className="hm-premio-selo">Promoção</span>}
        </span>
        <span className="hm-premio-nome">{premio.nome}</span>
      </button>

      <div className="hm-premio-preco">
        <strong>{formatarPontos(pts)} pts</strong>
        {promo && <s>{formatarPontos(pontosSemPromocao(premio, valorPonto))}</s>}
      </div>

      {cabe ? (
        <span className="hm-premio-cabe">Você já pode resgatar</span>
      ) : (
        <div className="hm-premio-falta">
          <span>
            Faltam <strong>{formatarPontos(pts - saldo)} pts</strong>
          </span>
          <div className="tk-progresso" aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <button
        type="button"
        className="tk-btn tk-btn--primario tk-btn--bloco hm-premio-add"
        onClick={() => onAdicionar(premio)}
      >
        <ShoppingCart size={17} aria-hidden="true" />
        Adicionar
      </button>
    </article>
  );
}

function PainelClienteHome() {
  const usuario = useMemo(() => lerUsuario(), []);
  const navigate = useNavigate();
  const location = useLocation();
  const alerta = location.state?.alerta || "";

  const { adicionarAoCarrinho } = useContext(CarrinhoContext);

  const [carregando, setCarregando] = useState(true);
  const [pontos, setPontos] = useState({ total: 0, reservados: 0, disponiveis: 0 });
  const [perfis, setPerfis] = useState([]);
  const [valorPonto, setValorPonto] = useState(0.03);
  const [premios, setPremios] = useState([]);
  const [campanhasAtivas, setCampanhasAtivas] = useState([]);
  const [resgates, setResgates] = useState([]);
  const [expiracoes, setExpiracoes] = useState([]);
  const [avisosResumo, setAvisosResumo] = useState(null);
  const [abrirExpiracao, setAbrirExpiracao] = useState(false);
  const [campanhaExperience, setCampanhaExperience] = useState(null);
  const [banners, setBanners] = useState([]);
  const [categoriasAtivas, setCategoriasAtivas] = useState(null);
  const [empresaAtiva, setEmpresaAtiva] = useState(true);
  const [aba, setAba] = useState(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [premioSelecionado, setPremioSelecionado] = useState(null);
  const [mostrarModalSorteio, setMostrarModalSorteio] = useState(false);
  const [eventoSlug, setEventoSlug] = useState(null);

  useEffect(() => {
    async function carregarDados() {
      if (!usuario?.id) return;
      try {
        const [
          pontosRes,
          perfisRes,
          configRes,
          premiosRes,
          campanhasRes,
          resgatesRes,
          avisosRes,
          experienceRes,
        ] = await Promise.all([
          axios.get(`/clientes/${usuario.id}/pontos`),
          axios.get("/perfis"),
          axios.get("/configuracoes/valor-ponto"),
          axios.get("/premios"),
          axios.get(`/campanhas/ativas-para-cliente/${usuario.id}`),
          axios.get(`/resgates/cliente/${usuario.id}`),
          axios.get(`/clientes/${usuario.id}/avisos-expiracao`),
          axios.get("/experience/campanhas"),
        ]);

        setValorPonto(configRes.data?.valor || 0.03);

        const { pontos_totais, pontos_reservados, pontos_disponiveis } = pontosRes.data || {};
        setPontos({
          total: Number(pontos_totais || 0),
          reservados: Number(pontos_reservados || 0),
          disponiveis: Number(pontos_disponiveis || 0),
        });

        setPerfis(
          Array.isArray(perfisRes.data)
            ? [...perfisRes.data].sort((a, b) => a.pontos_objetivo - b.pontos_objetivo)
            : []
        );

        setPremios((Array.isArray(premiosRes.data) ? premiosRes.data : []).filter(premioDisponivel));
        setCampanhasAtivas(Array.isArray(campanhasRes.data) ? campanhasRes.data : []);
        setResgates(Array.isArray(resgatesRes.data) ? resgatesRes.data : []);

        const campanhasExp = Array.isArray(experienceRes.data?.campanhas)
          ? experienceRes.data.campanhas
          : [];
        setCampanhaExperience(campanhasExp.find((c) => c.inscrito) || campanhasExp[0] || null);

        if (avisosRes.data?.expiraBreve) {
          setExpiracoes(avisosRes.data.detalhes || []);
          setAvisosResumo(avisosRes.data.resumo || null);
        } else {
          setExpiracoes([]);
          setAvisosResumo(null);
        }
      } catch (err) {
        console.error("Erro ao carregar dados da home:", err?.response?.data || err);
      } finally {
        setCarregando(false);
      }

      // Chamadas secundárias: uma falha aqui não derruba a Home
      axios
        .get("/categorias-premios")
        .then((res) =>
          setCategoriasAtivas(
            new Set((Array.isArray(res.data) ? res.data : []).filter((c) => c.ativa).map((c) => c.nome))
          )
        )
        .catch(() => setCategoriasAtivas(null));

      axios
        .get("/banner-home")
        .then((res) => setBanners(Array.isArray(res.data) ? res.data : []))
        .catch(() => setBanners([]));

      if (usuario.cnpj_empresa) {
        axios
          .get(`/empresas/cnpj/${usuario.cnpj_empresa}`)
          .then((res) => setEmpresaAtiva(Number(res.data?.ativa) === 1))
          .catch(() => {});
      }
    }

    carregarDados();
  }, [usuario?.id, usuario?.cnpj_empresa]);

  // Modal de sorteio para quem entrou pelo link de um evento
  useEffect(() => {
    const origem = localStorage.getItem("origem_login");
    const slug = localStorage.getItem("evento_slug");
    if (origem === "evento" && slug) {
      setEventoSlug(slug);
      setMostrarModalSorteio(true);
      localStorage.removeItem("origem_login");
      localStorage.removeItem("evento_slug");
    }
  }, []);

  // ---------- Nível ----------
  const nivel = useMemo(() => {
    const atual =
      [...perfis].reverse().find((p) => pontos.total >= p.pontos_objetivo) || { nome: "start", pontos_objetivo: 0 };
    const proximo = perfis.find((p) => pontos.total < p.pontos_objetivo) || null;
    const base = Number(atual.pontos_objetivo || 0);
    const pct = proximo
      ? Math.max(0, Math.min(100, ((pontos.total - base) / (proximo.pontos_objetivo - base)) * 100))
      : 100;
    return {
      nome: String(atual.nome || "start").toUpperCase(),
      proximo: proximo ? String(proximo.nome).toUpperCase() : null,
      faltam: proximo ? proximo.pontos_objetivo - pontos.total : 0,
      pct: Math.round(pct),
    };
  }, [perfis, pontos.total]);

  // ---------- Pedidos em andamento ----------
  const pedidosAndamento = useMemo(
    () =>
      resgates
        .filter((r) => !["enviado", "cancelado", "rejeitado"].includes(r.status))
        .sort((a, b) => new Date(b.data_resgate) - new Date(a.data_resgate)),
    [resgates]
  );

  // ---------- Prêmios por aba ----------
  const saldo = pontos.disponiveis;
  const cabemNoSaldo = useMemo(
    () => premios.filter((p) => calcularPontosPremio(p, valorPonto) <= saldo),
    [premios, saldo, valorPonto]
  );
  const abaAtual = aba || (cabemNoSaldo.length ? "saldo" : "novidades");

  const premiosDaAba = useMemo(() => {
    const pts = (p) => calcularPontosPremio(p, valorPonto);
    if (abaAtual === "saldo") {
      return [...cabemNoSaldo].sort((a, b) => pts(b) - pts(a)).slice(0, QTD_PREMIOS_HOME);
    }
    if (abaAtual === "populares") {
      return [...premios]
        .sort((a, b) => Number(b.total_pedidos || 0) - Number(a.total_pedidos || 0))
        .slice(0, QTD_PREMIOS_HOME);
    }
    return [...premios]
      .sort((a, b) => new Date(b.data_criacao || 0) - new Date(a.data_criacao || 0))
      .slice(0, QTD_PREMIOS_HOME);
  }, [abaAtual, cabemNoSaldo, premios, valorPonto]);

  // ---------- Categorias com prêmios disponíveis ----------
  // Só entram categorias com pelo menos um prêmio em estoque (e ativas no
  // admin, quando a lista de categorias carrega). As com mais prêmios vêm primeiro.
  const categoriasHome = useMemo(() => {
    const contagem = new Map();
    premios.forEach((p) => {
      if (!p.categoria) return;
      if (categoriasAtivas && !categoriasAtivas.has(p.categoria)) return;
      contagem.set(p.categoria, (contagem.get(p.categoria) || 0) + 1);
    });
    return [...contagem.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "pt-BR"))
      .slice(0, MAX_CATEGORIAS_HOME)
      .map(([nome, qtd]) => ({ nome, qtd, ...visualDaCategoria(nome) }));
  }, [premios, categoriasAtivas]);

  const abrirPremio = (premio) => {
    setPremioSelecionado(premio);
    setModalAberto(true);
  };

  const adicionar = (premio, qtd = 1) => {
    adicionarAoCarrinho(premio, qtd);
    toast.success(`"${premio.nome}" adicionado ao carrinho.`);
  };

  if (!usuario) return <Navigate to="/login" replace />;

  const temExpiracao = expiracoes.length > 0;

  return (
    <div className="hm">
      {alerta && <div className="hm-alerta" role="status">{alerta}</div>}

      {/* ===== Saldo + avisos ===== */}
      <section className="hm-topo">
        <div className="hm-saldo" data-tour="saldo">
          <div className="hm-saldo-cabecalho">
            <h1 className="hm-ola">Olá, {primeiroNome(usuario.nome)}!</h1>
            <span className="hm-nivel">Nível {nivel.nome}</span>
          </div>

          <div className="hm-saldo-numeros">
            <div>
              <span className="hm-saldo-rotulo">Pontos disponíveis para trocar</span>
              <strong className="hm-saldo-valor">
                {carregando ? "…" : formatarPontos(pontos.disponiveis)}
              </strong>
            </div>
            <dl className="hm-saldo-extras">
              <div>
                <dt>Reservados</dt>
                <dd>{formatarPontos(pontos.reservados)}</dd>
              </div>
              <div>
                <dt>Total acumulado</dt>
                <dd>{formatarPontos(pontos.total)}</dd>
              </div>
            </dl>
          </div>

          <div className="hm-nivel-progresso">
            <div className="hm-nivel-texto">
              {nivel.proximo ? (
                <span>
                  Faltam <strong>{formatarPontos(nivel.faltam)} pts</strong> para o nível {nivel.proximo}
                </span>
              ) : (
                <span>Você está no nível máximo</span>
              )}
              <span>{nivel.pct}%</span>
            </div>
            <div className="tk-progresso hm-progresso-escuro" aria-hidden="true">
              <span style={{ width: `${nivel.pct}%` }} />
            </div>
          </div>

          <div className="hm-saldo-acoes">
            <Link to="/painel-cliente/catalogo-premios" className="tk-btn hm-btn-claro">
              <Gift size={18} aria-hidden="true" />
              Trocar meus pontos
            </Link>
            {empresaAtiva ? (
              <Link to="/painel-cliente/cadastrar-operacao" className="tk-btn hm-btn-contorno">
                <FileEdit size={18} aria-hidden="true" />
                Cadastrar operação
              </Link>
            ) : (
              <span className="hm-empresa-inativa">
                Sua empresa está fora do programa no momento. O cadastro de operações está indisponível.
              </span>
            )}
            <Link to="/painel-cliente/meus-pontos" className="hm-link-claro">
              Ver extrato
            </Link>
          </div>
        </div>

        <div className="hm-lateral" data-tour="avisos">
          {temExpiracao && (
            <div className="hm-expira">
              <span className="hm-expira-icone" aria-hidden="true">
                <Clock size={20} />
              </span>
              <div className="hm-expira-corpo">
                <strong>
                  {formatarPontos(avisosResumo?.total_pontos || expiracoes.reduce((a, e) => a + Number(e.pontos || 0), 0))} pontos
                  vencem em breve
                </strong>
                {avisosResumo?.proxima_data && (
                  <span>Próximo vencimento: {avisosResumo.proxima_data}</span>
                )}
                <div className="hm-expira-acoes">
                  <Link to="/painel-cliente/catalogo-premios">Usar agora</Link>
                  <button
                    type="button"
                    onClick={() => setAbrirExpiracao((v) => !v)}
                    aria-expanded={abrirExpiracao}
                  >
                    {abrirExpiracao ? "Ocultar datas" : "Ver datas"}
                    <ChevronDown size={15} className={abrirExpiracao ? "is-aberto" : ""} aria-hidden="true" />
                  </button>
                </div>
                {abrirExpiracao && (
                  <ul className="hm-expira-lista">
                    {expiracoes.map((e, i) => (
                      <li key={i}>
                        <span>{e.data}</span>
                        <strong>{formatarPontos(e.pontos)} pts</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="hm-pedidos">
            <div className="hm-pedidos-topo">
              <h2>Seus pedidos</h2>
              <Link to="/painel-cliente/meus-resgates">Ver todos</Link>
            </div>
            {pedidosAndamento.length === 0 ? (
              <p className="hm-pedidos-vazio">
                Nenhum pedido em andamento. Quando você resgatar um prêmio, a entrega aparece aqui.
              </p>
            ) : (
              <ul className="hm-pedidos-lista">
                {pedidosAndamento.slice(0, 3).map((r) => {
                  const st = STATUS_RESGATE[r.status] || { texto: r.status, tom: "" };
                  return (
                    <li key={`${r.id}-${r.nome_premio}`}>
                      <span className="hm-pedido-foto">
                        {r.imagem_premio ? (
                          <img src={getImagemUrl(r.imagem_premio)} alt="" loading="lazy" />
                        ) : (
                          <Gift size={18} aria-hidden="true" />
                        )}
                      </span>
                      <span className="hm-pedido-info">
                        <strong>{r.nome_premio}</strong>
                        <span>
                          Pedido #{r.id} · {formatarData(r.data_resgate)}
                        </span>
                      </span>
                      <span className={`tk-etiqueta ${st.tom ? `tk-etiqueta--${st.tom}` : ""}`}>{st.texto}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* ===== Banner + Experience ===== */}
      {(banners.length > 0 || campanhaExperience) && (
        <section className={`hm-destaques ${banners.length && campanhaExperience ? "" : "is-unico"}`}>
          {banners.length > 0 && (
            <div className="hm-banner">
              <Slider
                dots={banners.length > 1}
                infinite={banners.length > 1}
                speed={600}
                autoplay={banners.length > 1}
                autoplaySpeed={6000}
                pauseOnHover
                arrows={false}
              >
                {banners.map((banner) => {
                  const link = banner.link && banner.link !== "#" ? banner.link : null;
                  const externo = link?.startsWith("http");
                  const imagem = (
                    <img src={getImagemUrl(banner.url)} alt={banner.titulo || "Banner do TikPontos"} />
                  );
                  return (
                    <div key={banner.id} className="hm-banner-slide">
                      {link ? (
                        externo ? (
                          <a href={link} target="_blank" rel="noopener noreferrer">
                            {imagem}
                          </a>
                        ) : (
                          <Link to={link}>{imagem}</Link>
                        )
                      ) : (
                        imagem
                      )}
                    </div>
                  );
                })}
              </Slider>
            </div>
          )}

          {campanhaExperience && (
            <div className="hm-experience">
              <span className="hm-experience-marca">Tikpag Experience</span>
              <h2>
                {campanhaExperience.inscrito ? "Sua jornada já começou" : "Operações que viram experiências"}
              </h2>
              <p>
                {campanhaExperience.inscrito
                  ? "Acompanhe seu volume do mês, veja o próximo PIN e consulte suas conquistas."
                  : "Participe, conquiste PINs exclusivos e escolha experiências especiais todos os meses."}
              </p>
              <div className="hm-experience-meta">
                <span>{campanhaExperience.quantidade_pins || 0} PINs</span>
                <span>Apuração mensal</span>
                <span>{campanhaExperience.inscrito ? "Inscrição confirmada" : "Inscrições abertas"}</span>
              </div>
              <button
                type="button"
                className="tk-btn hm-experience-btn"
                onClick={() => navigate("/painel-cliente/tikpag-experience")}
              >
                {campanhaExperience.inscrito ? "Ver minha jornada" : "Conhecer e participar"}
              </button>
            </div>
          )}
        </section>
      )}

      {/* ===== Categorias (só as que têm prêmios disponíveis) ===== */}
      {categoriasHome.length > 0 && (
        <section className="hm-secao" data-tour="categorias">
          <div className="hm-secao-topo">
            <h2 className="tk-secao-titulo">Explorar por categoria</h2>
            <Link to="/painel-cliente/catalogo-premios" className="hm-ver-catalogo">
              Todas as categorias
            </Link>
          </div>
          <div className="hm-categorias">
            {categoriasHome.map(({ nome, qtd, icone: Icone, tom }) => (
              <Link
                key={nome}
                to={`/painel-cliente/catalogo-premios?categoria=${encodeURIComponent(nome)}`}
                className="hm-categoria"
              >
                <span className={`hm-categoria-icone is-${tom}`} aria-hidden="true">
                  <Icone size={20} />
                </span>
                <span className="hm-categoria-textos">
                  <span>{nome}</span>
                  <small>
                    {qtd} {qtd === 1 ? "prêmio" : "prêmios"}
                  </small>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ===== Prêmios ===== */}
      <section className="hm-secao" data-tour="premios">
        <div className="hm-secao-topo">
          <h2 className="tk-secao-titulo">Prêmios para você</h2>
          <div className="hm-abas" role="group" aria-label="Filtrar prêmios">
            {ABAS.map((a) => (
              <button
                key={a.id}
                type="button"
                className="tk-chip"
                aria-pressed={abaAtual === a.id}
                onClick={() => setAba(a.id)}
              >
                {a.label}
              </button>
            ))}
            <Link to="/painel-cliente/catalogo-premios" className="hm-ver-catalogo">
              Ver catálogo completo
            </Link>
          </div>
        </div>

        {carregando ? (
          <div className="hm-premios">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="tk-skeleton hm-premio-skeleton" />
            ))}
          </div>
        ) : premiosDaAba.length ? (
          <div className="hm-premios">
            {premiosDaAba.map((p) => (
              <CartaoPremio
                key={p.id}
                premio={p}
                valorPonto={valorPonto}
                saldo={saldo}
                onAbrir={abrirPremio}
                onAdicionar={(premio) => adicionar(premio, 1)}
              />
            ))}
          </div>
        ) : (
          <div className="tk-vazio">
            <strong>Ainda não há prêmios dentro do seu saldo</strong>
            <span>Veja as novidades ou cadastre operações para acumular mais pontos.</span>
            <button type="button" className="tk-btn tk-btn--secundario" onClick={() => setAba("novidades")}>
              Ver novidades
            </button>
          </div>
        )}
      </section>

      {/* ===== Campanhas ===== */}
      {campanhasAtivas.length > 0 && (
        <section className="hm-secao">
          <div className="hm-secao-topo">
            <h2 className="tk-secao-titulo">Campanhas abertas para você</h2>
            <Link to="/painel-cliente/minhas-campanhas" className="hm-ver-catalogo">
              Minhas campanhas
            </Link>
          </div>
          <div className="hm-campanhas">
            {campanhasAtivas.map((c) => (
              <article key={c.id} className="hm-campanha">
                <div className="hm-campanha-imagem">
                  {c.imagem ? <img src={getImagemUrl(c.imagem)} alt="" loading="lazy" /> : <Trophy size={30} aria-hidden="true" />}
                </div>
                <div className="hm-campanha-corpo">
                  <div className="hm-campanha-topo">
                    <h3>{c.titulo}</h3>
                    {c.data_conquista ? (
                      <span className="tk-etiqueta tk-etiqueta--verde">Conquistada</span>
                    ) : c.inscricao_ativa ? (
                      <span className="tk-etiqueta tk-etiqueta--azul">Inscrito</span>
                    ) : (
                      <span className="tk-etiqueta">Inscrições abertas</span>
                    )}
                  </div>
                  {c.descricao && <p className="hm-campanha-desc">{c.descricao}</p>}
                  <dl className="hm-campanha-dados">
                    <div>
                      <dt>Período</dt>
                      <dd>
                        {formatarData(c.data_inicio)} a {formatarData(c.data_fim)}
                      </dd>
                    </div>
                    <div>
                      <dt>Meta</dt>
                      <dd>{formatarPontos(c.pontos_objetivo)} pontos</dd>
                    </div>
                    {c.recompensa && (
                      <div>
                        <dt>Recompensa</dt>
                        <dd>{c.recompensa}</dd>
                      </div>
                    )}
                  </dl>
                  <button
                    type="button"
                    className="tk-btn tk-btn--secundario tk-btn--bloco"
                    disabled={!!c.data_conquista}
                    onClick={() => navigate(`/painel-cliente/campanhas/${c.id}/inscricao`)}
                  >
                    {c.data_conquista ? "Já conquistada" : c.inscricao_ativa ? "Ver minha inscrição" : "Participar"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <ModalPremioDetalhe
        premio={premioSelecionado}
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        onAdicionar={(premio, qtd) => {
          adicionar(premio, qtd);
          setModalAberto(false);
        }}
        pontosProduto={calcularPontosPremio(premioSelecionado, valorPonto)}
        pontosOriginais={premioSelecionado ? pontosSemPromocao(premioSelecionado, valorPonto) : 0}
        saldoDisponivel={saldo}
      />

      {mostrarModalSorteio && (
        <ModalSorteioEvento slug={eventoSlug} onClose={() => setMostrarModalSorteio(false)} />
      )}
    </div>
  );
}

export default React.memo(PainelClienteHome);
